import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Share2, Heart } from 'lucide-react';
import { products } from '../data/mockData';
import { useAppContext } from '../context/AppContext';
import { useDragScroll } from '../hooks/useDragScroll';
export function ProductDetail() {
  const similar = useDragScroll<HTMLDivElement>();
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    selectedCountry
  } = useAppContext();
  const product = products.find(p => p.id === id);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  if (!product) {
    return <div className="flex-1 flex flex-col items-center justify-center p-8 text-center min-h-full" data-id="element-112">
        <h2 className="font-display text-2xl mb-4" data-id="element-113">Product Not Found</h2>
        <button onClick={() => navigate(-1)} className="text-sm font-bold uppercase tracking-wider underline" data-id="element-114">
          Go Back
        </button>
      </div>;
  }
  const allImages = [product.image, ...product.thumbnails];
  const sizes = product.category === 'Cleats' ? ['7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '12'] : ['S', 'M', 'L', 'XL', 'XXL'];
  const similarProducts = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);
  return <div className="relative flex flex-col w-full min-h-full bg-zinc-950" data-id="element-115">
      {/* Nav */}
      <div className="absolute top-11 left-0 w-full z-30 flex justify-between items-center p-4" data-id="element-116">
        <button onClick={() => navigate(-1)} className="w-10 h-10 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10" data-id="element-117">
          <ChevronLeft className="w-6 h-6" data-id="element-118" />
        </button>
        <div className="flex gap-2" data-id="element-119">
          <button className="w-10 h-10 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10" data-id="element-120">
            <Share2 className="w-5 h-5" data-id="element-121" />
          </button>
          <button className="w-10 h-10 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10" data-id="element-122">
            <Heart className="w-5 h-5" data-id="element-123" />
          </button>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="relative aspect-[4/5] w-full bg-zinc-900" data-id="element-124">
        <img src={allImages[activeImage]} alt={product.name} className="w-full h-full object-cover" data-id="element-125" />

        {/* Thumbnails */}
        <div className="absolute bottom-4 left-0 w-full px-4 flex gap-2 justify-center" data-id="element-126">
          {allImages.map((img, idx) => <button key={idx} onClick={() => setActiveImage(idx)} className={`w-2 h-2 rounded-full transition-all ${activeImage === idx ? 'bg-white w-6' : 'bg-white/50'}`} data-id="element-127" />)}
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4 flex flex-col gap-6" data-id="element-128">
        <div data-id="element-129">
          <div className="flex justify-between items-start mb-1" data-id="element-130">
            <p className="text-sm font-bold text-zinc-400 uppercase tracking-wider" data-id="element-131">
              {product.subtitle}
            </p>
            <span className="font-display text-2xl" data-id="element-132">${product.price}</span>
          </div>
          <h1 className="font-display text-4xl leading-none mb-2" data-id="element-133">
            {product.name}
          </h1>
        </div>

        {/* Size Selector */}
        {product.category !== 'Gear' && <div data-id="element-134">
            <div className="flex justify-between items-center mb-3" data-id="element-135">
              <h3 className="text-sm font-bold uppercase tracking-wider" data-id="element-136">
                Select Size
              </h3>
              <button className="text-xs text-zinc-500 underline uppercase" data-id="element-137">
                Size Guide
              </button>
            </div>
            <div className="grid grid-cols-5 gap-2.5" data-id="element-138">
              {sizes.map(size => <button key={size} onClick={() => setSelectedSize(size)} className={`h-[52px] border flex items-center justify-center text-sm font-bold transition-colors ${selectedSize === size ? 'border-white bg-white text-black' : 'border-zinc-800 bg-zinc-900 text-white hover:border-zinc-600'}`} data-id="element-139">
                  {size}
                </button>)}
            </div>
          </div>}

        {/* Description */}
        <div data-id="element-140">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-2" data-id="element-141">
            Description
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed" data-id="element-142">
            {product.description}
          </p>
        </div>

        {/* Hype Details */}
        <div className="border-t border-b border-zinc-900 py-4 my-2" data-id="element-143">
          <ul className="space-y-2 text-sm text-zinc-300" data-id="element-144">
            <li className="flex items-center gap-2" data-id="element-145">
              <div className="w-1.5 h-1.5 bg-[#e11d2a] rounded-full" data-id="element-146" />
              Regular fit
            </li>
            <li className="flex items-center gap-2" data-id="element-147">
              <div className="w-1.5 h-1.5 bg-[#e11d2a] rounded-full" data-id="element-148" />
              Ribbed crewneck
            </li>
            <li className="flex items-center gap-2" data-id="element-149">
              <div className="w-1.5 h-1.5 bg-[#e11d2a] rounded-full" data-id="element-150" />
              100% recycled polyester
            </li>
          </ul>
        </div>

        {/* Similar Products */}
        {similarProducts.length > 0 && <div data-id="element-151">
            <h3 className="font-display text-xl mb-4" data-id="element-152">You Might Also Like</h3>
            <div ref={similar.ref} {...similar.dragProps} className="flex overflow-x-auto no-scrollbar gap-4 pb-4 -mx-4 px-4 select-none" data-id="element-153">
              {similarProducts.map(p => <div key={p.id} onClick={() => {
            navigate(`/product/${p.id}`);
            window.scrollTo(0, 0);
          }} className="shrink-0 w-[160px] flex flex-col group cursor-pointer" data-id="element-154">
                  <div className="relative aspect-[3/4] bg-zinc-900 mb-2 overflow-hidden" data-id="element-155">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" data-id="element-156" />
                  </div>
                  <h4 className="font-bold text-xs uppercase truncate" data-id="element-157">
                    {p.name}
                  </h4>
                  <span className="font-display text-sm" data-id="element-158">${p.price}</span>
                </div>)}
            </div>
          </div>}
      </div>

      {/* Sticky Bottom Action — pins to the bottom of the scrolling screen */}
      <div className="sticky bottom-0 left-0 w-full z-40 p-4 pb-6 bg-gradient-to-t from-zinc-950 via-zinc-950 to-transparent pt-8" data-id="element-159">
        <button className="w-full bg-white text-black h-14 flex items-center justify-between px-6 font-display text-xl hover:bg-zinc-200 transition-colors shadow-[0_0_40px_rgba(0,0,0,0.5)]" data-id="element-160">
          <span data-id="element-161">ADD TO BAG</span>
          <ArrowRightIcon data-id="element-162" />
        </button>
      </div>
    </div>;
}
function ArrowRightIcon() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-163">
      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" data-id="element-164" />
    </svg>;
}